import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuBell } from 'react-icons/lu';

import { NotificationsPanel } from 'components/notifications-panel';
import { useAuth } from 'context/AuthContext';
import { NotificationRow, destinoDoAviso, familyAccent, rowText, rowTitle } from 'pages/Notificacoes/notification-inbox';
import { getNotificationInbox, getNotificationUnreadCount } from 'services/notificationInbox.service';
import { FOCO_VISIVEL } from 'utils/foco';

import { SectionHeading } from './section-heading';

/** A partir daqui o sino mostra "99+" (SINO-02). */
const MAIOR_NUMERO_NO_SINO = 99;

const ROTULO_DO_SINO = 'Ver todas as notificações';

/** O nome acessível do sino: o número visível é escondido do leitor de tela e vem por extenso aqui (SINO-04). */
function rotuloDoSino(naoLidas: number | null) {
  if (!naoLidas) return ROTULO_DO_SINO;
  if (naoLidas > MAIOR_NUMERO_NO_SINO) return `${ROTULO_DO_SINO}, mais de ${MAIOR_NUMERO_NO_SINO} não lidas`;

  return `${ROTULO_DO_SINO}, ${naoLidas} ${naoLidas === 1 ? 'não lida' : 'não lidas'}`;
}

/**
 * Faixa "Acompanhe suas notificações" da Home (HOME-40 a HOME-42): o aviso não lido mais recente.
 *
 * Uma leitura só, `get_notification_inbox` com `{ status: 'unread', limit: 1 }`: o `$match` casa o
 * índice {player, status, created} e o `$sort` sai dele, então custa o mesmo que o contador que
 * havia aqui — e a resposta vazia já diz que não há aviso. Sem aviso não lido, a seção some.
 *
 * "Ver todas as notificações" abre o painel lateral do design (`NotificationsPanel`). Abrir a faixa
 * não marca o aviso como lido; quem marca é o painel, ao clicar no aviso. Ao fechar o painel o aviso
 * é relido, porque o que estava aqui pode ter sido lido lá.
 *
 * O sino mostra quantos não lidos há, com uma leitura própria (`get_notification_unread_count`). Ela
 * sai depois do aviso, e só quando há o que mudar: um aviso mais recente que o da última contagem,
 * ou o painel fechado (SINO-03, SINO-08). Um refresh do jogador que não traz aviso novo não reconta,
 * e uma contagem que falha mantém o número que já estava (SINO-07).
 */
export function NotificationsRow({ waitingRefresh = false }: { waitingRefresh?: boolean }) {
  // `userRevision` sobe a cada refreshUser concluído (ex.: websocket de notificação), e só então
  // o aviso é relido (DCE-25). Com `waitingRefresh` (volta com `direction: 'back'`), a leitura
  // da montagem espera esse refresh terminar, para sair uma vez só (DCE-57).
  const { userRevision } = useAuth();
  const [latest, setLatest] = useState<NotificationRow | null>(null);
  const [naoLidas, setNaoLidas] = useState<number | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelClosings, setPanelClosings] = useState(0);
  const headingId = useId();
  const ultimaContagemRef = useRef<{ avisoId: string; fechamentos: number } | null>(null);

  useEffect(() => {
    if (waitingRefresh) return;

    let cancelled = false;

    function recontarSePreciso(maisRecente: NotificationRow | null) {
      if (!maisRecente) return;

      const anterior = ultimaContagemRef.current;
      if (anterior?.avisoId === maisRecente._id && anterior.fechamentos === panelClosings) return;

      ultimaContagemRef.current = { avisoId: maisRecente._id, fechamentos: panelClosings };
      getNotificationUnreadCount().then(contagem => {
        if (!cancelled && contagem !== null) setNaoLidas(contagem);
      });
    }

    getNotificationInbox({ status: 'unread', limit: 1 })
      .then(rows => {
        if (cancelled) return;
        setLatest(rows[0] ?? null);
        recontarSePreciso(rows[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setLatest(null);
      });

    return () => {
      cancelled = true;
    };
  }, [userRevision, waitingRefresh, panelClosings]);

  function closePanel() {
    setPanelOpen(false);
    setPanelClosings(count => count + 1);
  }

  const panel = <NotificationsPanel isOpen={panelOpen} onClose={closePanel} />;

  // O painel continua montado quando o último aviso não lido some, para fechar normalmente.
  if (!latest) return panel;

  const text = rowText(latest);

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2.5">
      <SectionHeading id={headingId}>Acompanhe suas notificações</SectionHeading>

      {/* D3: 34px não tem degrau no Tailwind, e min-w não tem escala de spacing no 3.3. */}
      {/* A cor do assunto (a da faixa do e-mail, NOTIF-20) é o fundo do cartão, como nos cartões do
          painel: como cor de texto sobre o branco, nenhuma das quatro passa nem no mínimo de texto
          grande (3:1). Sobre elas, o `brand-blue-dark` dá de 4,81:1 a 5,87:1. */}
      <div
        data-testid="preview-da-notificacao"
        className={`flex flex-wrap items-center gap-3.5 rounded-jj-lg px-5 py-4 text-content shadow-jj-01 sm:px-[34px] ${familyAccent(latest.family)}`}
      >
        <p className="font-display text-2xl font-bold leading-tight text-brand-blue-dark">{rowTitle(latest)}</p>

        <p className="min-w-[240px] flex-1 text-jj-body-sm font-semibold leading-normal">
          {text && `${text} `}
          <CheckLink url={latest.inapp?.cta_url} onOpenPanel={() => setPanelOpen(true)} />
        </p>

        <button
          type="button"
          aria-haspopup="dialog"
          aria-label={rotuloDoSino(naoLidas)}
          onClick={() => setPanelOpen(true)}
          className={`flex cursor-pointer items-center gap-2 whitespace-nowrap border-none bg-transparent p-0 font-text text-jj-label font-bold uppercase tracking-[.03em] text-brand-blue-dark hover:underline ${FOCO_VISIVEL}`}
        >
          <span data-testid="icone-do-sino" className="relative inline-flex">
            <LuBell size={15} aria-hidden="true" />
            {!!naoLidas && (
              <span
                aria-hidden="true"
                data-testid="numero-do-sino"
                className="absolute -right-2 -top-2 flex h-4 min-w-[16px] items-center justify-center rounded-jj-pill bg-brand-blue px-1 font-display text-[10px] font-bold leading-none tracking-normal text-brand-yellow"
              >
                {naoLidas > MAIOR_NUMERO_NO_SINO ? `${MAIOR_NUMERO_NO_SINO}+` : naoLidas}
              </span>
            )}
          </span>
          {ROTULO_DO_SINO}
        </button>
      </div>

      {panel}
    </section>
  );
}

/** "Confira aqui!": o destino do aviso, externo em nova aba; sem destino válido, o painel de notificações (DEST-17). */
function CheckLink({ url, onOpenPanel }: { url?: string; onOpenPanel: () => void }) {
  // `brand-blue` sobre as cores do assunto fica abaixo de 4,5:1; o `brand-blue-dark` passa nas quatro.
  const className = 'text-brand-blue-dark underline decoration-2 hover:decoration-[3px]';
  const destino = destinoDoAviso(url);

  if (destino?.tipo === 'externo') {
    return (
      <a href={destino.url} target="_blank" rel="noreferrer" className={className}>
        Confira aqui!
      </a>
    );
  }

  if (destino?.tipo === 'interno') {
    return (
      <Link to={destino.url} className={className}>
        Confira aqui!
      </Link>
    );
  }

  return (
    <button
      type="button"
      aria-haspopup="dialog"
      onClick={onOpenPanel}
      className={`cursor-pointer border-none bg-transparent p-0 font-semibold ${className} ${FOCO_VISIVEL}`}
    >
      Confira aqui!
    </button>
  );
}
